import proj4 from 'proj4';
import { BufferGeometry, Points, Vector3 } from 'three';

import { loadCesiumPnts } from '../../../../loader/cesium/content/pnts/pnts-loader';
import {
  CesiumPnts,
  PntsBatchTable,
  PntsFeatureTable,
} from '../../../../loader/cesium/content/pnts/types';
import { TileNode } from '../../../../loader/cesium/tile-set/types';
import { getFeatureAttributeName } from './attributes';
import { BufferAttributeAdapter } from './buffer-attribute/buffer-attribute-adapter';
import { ClassificationBufferAttribute } from './buffer-attribute/classification-buffer-attribute';
import { DefaultColorBufferAttribute } from './buffer-attribute/color-buffer-attribute';
import { NormalBufferAttribute } from './buffer-attribute/normal-buffer-attribute';
import { PointSizeAttribute } from './buffer-attribute/points-size-attribute';
import {
  DefaultPositionBufferAttribute,
  TransformPositionBufferAttribute,
} from './buffer-attribute/position-buffer-attribute';
import { CesiumPoints } from './cesium-points';
import { PointsFactory } from './types';

export interface PointBuilderOptions {
  transformPointsToProjection?: string;
  geometricError?: number;
}

/**
 *  Returns a factory for creating points
 * @param options
 */
export const createPointsFactory = async (
  options: PointBuilderOptions = {},
  // eslint-disable-next-line @typescript-eslint/require-await
): Promise<PointsFactory> => {
  // Arbitrary scale of the point sizes to fill the point cloud area coverage
  const pointSizeScale = 1.5;

  return async (tileNode: TileNode, pntsBuffer?: ArrayBuffer): Promise<Points> => {
    const pntsData = await loadCesiumPnts(pntsBuffer || tileNode.content!.signedUrl!);

    return new PointBuilder(
      pntsData,
      resolveBufferAttributeAdapters(options),
      Object.assign({}, options, {
        geometricError: tileNode.geometricError * pointSizeScale,
      }),
    ).buildPoints();
  };
};

// Declare which attributes currently is supported
const supportedAttributes: (keyof PntsFeatureTable | keyof PntsBatchTable)[] = [
  'POSITION',
  'RGB',
  'RGBA',
  'Classification',
  'NORMAL',
];
const isSupportedPntsFeature = (attribute: string): attribute is keyof PntsFeatureTable => {
  return supportedAttributes.includes(attribute as keyof PntsFeatureTable);
};

/**
 *  This builder extracts the data from the Cesium pnts file
 *  and returns a THREE Points object
 */
class PointBuilder {
  private bufferGeometry: BufferGeometry = new BufferGeometry();

  constructor(
    private pntsData: CesiumPnts,
    private bufferAttributeAdapters: Map<
      keyof PntsFeatureTable | keyof PntsBatchTable,
      BufferAttributeAdapter
    >,
    private options: PointBuilderOptions,
  ) {}

  buildPoints(): Points {
    // Always include 'Classification' even if does not exist in the data.
    for (const feature of [...Object.keys(this.pntsData.features.featureTable), 'Classification']) {
      if (!isSupportedPntsFeature(feature)) {
        continue;
      }

      this.bufferGeometry.setAttribute(
        getFeatureAttributeName(feature),
        this.bufferAttributeAdapters.get(feature)!(feature, this.pntsData, this.options),
      );
    }

    this.bufferGeometry.setAttribute(
      'point_size',
      PointSizeAttribute(this.pntsData, this.options.geometricError ?? 1),
    );

    this.bufferGeometry.computeBoundingSphere();

    return new CesiumPoints(this.bufferGeometry, this.options.geometricError ?? 1);
  }

  /**
   *  Returns a point matching the project's position
   */
  resolvePointsPosition() {
    const rtcCenter = this.options.transformPointsToProjection
      ? proj4(
          'wgs84m',
          this.options.transformPointsToProjection,
          this.pntsData.features.featureTable.RTC_CENTER,
        )
      : this.pntsData.features.featureTable.RTC_CENTER;

    return new Vector3(rtcCenter[0], rtcCenter[1], rtcCenter[2]);
  }
}

/**
 *  Returns buffer attribute adapters used for parsing Cesium pnts files.
 *  @param options The options sent in when building the points builder
 */
const resolveBufferAttributeAdapters = (
  options: PointBuilderOptions,
): Map<keyof PntsFeatureTable | keyof PntsBatchTable, BufferAttributeAdapter> => {
  const bufferAttributeAdapters = new Map<
    keyof PntsFeatureTable | keyof PntsBatchTable,
    BufferAttributeAdapter
  >();

  if (options.transformPointsToProjection !== undefined) {
    bufferAttributeAdapters.set('POSITION', TransformPositionBufferAttribute);
  } else {
    bufferAttributeAdapters.set('POSITION', DefaultPositionBufferAttribute);
  }

  bufferAttributeAdapters.set('Classification', ClassificationBufferAttribute);

  bufferAttributeAdapters.set('RGB', DefaultColorBufferAttribute);
  bufferAttributeAdapters.set('RGBA', DefaultColorBufferAttribute);
  bufferAttributeAdapters.set('NORMAL', NormalBufferAttribute);

  return bufferAttributeAdapters;
};
